import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, User, ShieldCheck, RefreshCw } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/api";
import FilaZeroTimeSelect from "../components/FilaZeroTimeSelect";

const estadosUF = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"
];

const removeAcentos = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9\s._-]/g, "")
    .trim();

const slugifyDominio = (str) =>
  removeAcentos(str)
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

const maskTelefone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 14);
  }
  return digits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .slice(0, 15);
};

const maskCep = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.replace(/(\d{5})(\d)/, "$1-$2").slice(0, 9);
};

const maskCpfCnpj = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const buscarEnderecoPorCep = async (cep) => {
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep.replace(/\D/g, '')}/json/`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.erro) return null;
    return data;
  } catch {
    return null;
  }
};

const handleCepBlur = async (e, setEstabelecimento) => {
  const cep = e.target.value;
  if (cep && cep.replace(/\D/g, '').length === 8) {
    const endereco = await buscarEnderecoPorCep(cep);
    if (endereco) {
      setEstabelecimento((prev) => ({
        ...prev,
        endereco: endereco.logradouro || '',
        bairro: endereco.bairro || '',
        cidade: endereco.localidade || '',
        estado: endereco.uf || '',
      }));
    }
  }
};

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const gerarSugestoes = (nome) => {
  const normalizado = removeAcentos(nome).toLowerCase();
  const partes = normalizado.split(/\s+/).filter(Boolean);
  if (partes.length === 0) return [];
  const first = partes[0];
  const last = partes[partes.length - 1] || "";
  const base = [
    `${first}${last}`,
    `${first}.${last}`,
    `${first}_${last}`,
    `${first}${last.slice(0, 1)}`,
    `${first}${last}${new Date().getFullYear()}`,
    `${first}${last}01`,
    `${first}${last}02`,
    `${first}${last}03`,
  ];

  const unique = Array.from(new Set(base.map((s) => s.replace(/\.+$/g, ""))))
    .filter(Boolean)
    .slice(0, 10);
  return unique;
};

const gerarSugestoesDominio = (nome, cidade) => {
  const base = slugifyDominio(nome);
  if (!base) return [];
  const cidadeSlug = slugifyDominio(cidade || "") || "app";
  const variantes = [
    base,
    `${base}_${cidadeSlug}`,
    `${base}_${new Date().getFullYear()}`,
    `${base}_01`,
  ];

  if (base.length < 4) {
    variantes.push(`${base}_${cidadeSlug}_oficial`);
  }

  return Array.from(new Set(variantes)).filter(Boolean).slice(0, 6);
};

const NovoEstabelecimento = () => {
  const navigate = useNavigate();
  const [accessLoading, setAccessLoading] = useState(true);

  const Spinner = ({ size = 16 }) => (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        border: "2px solid rgba(0,0,0,0.2)",
        borderTopColor: "currentColor",
        borderRadius: "50%",
        animation: "fz-spin 0.6s linear infinite",
      }}
    />
  );

  const [tipos, setTipos] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [estabelecimento, setEstabelecimento] = useState({
    nome: "",
    dominio: "",
    tipo_id: "",
    descricao: "",
    endereco: "",
    numero: "",
    bairro: "",
    cidade: "",
    estado: "",
    cep: "",
    telefone: "",
    email: "",
    documento_faturamento: "",
    instagram: "",
    facebook: "",
    whatsapp: "",
    site: "",
    hora_abertura: "",
    hora_fechamento: "",
    imagem_logo: "",
  });

  const [adminUser, setAdminUser] = useState({
    nome: "",
    usuario: "",
    email: "",
    telefone: "",
  });

  const [criarProfissionalAdmin, setCriarProfissionalAdmin] = useState(true);
  const [profissionalAdmin, setProfissionalAdmin] = useState({
    nome: "",
    telefone: "",
  });

  const [erros, setErros] = useState({});
  const [usernameStatus, setUsernameStatus] = useState("idle");
  const [emailStatus, setEmailStatus] = useState("idle");
  const [sugestoes, setSugestoes] = useState([]);
  const [sugestoesStatus, setSugestoesStatus] = useState({});
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  
  const [dominioStatus, setDominioStatus] = useState("idle");
  const [sugestoesDominioStatus, setSugestoesDominioStatus] = useState({});
  const [mostrarSugestoesDominio, setMostrarSugestoesDominio] = useState(false);
  
  

  const sugestoesDominio = useMemo(
    () => gerarSugestoesDominio(estabelecimento.nome, estabelecimento.cidade),
    [estabelecimento.nome, estabelecimento.cidade]
  );

  const abrirSugestoesDominio = () => {
    if (!sugestoesDominio.length) {
      toast.info("Informe o nome fantasia para gerar sugestões de domínio.");
      return;
    }
    setMostrarSugestoesDominio(true);
  };

  useEffect(() => {
    if (!document.getElementById("novo-estabelecimento-styles")) {
      const style = document.createElement("style");
      style.id = "novo-estabelecimento-styles";
      style.innerHTML = "@keyframes fz-spin { to { transform: rotate(360deg); } }";
      document.head.appendChild(style);
    }

    const validarAcesso = async () => {
      try {
        const res = await api.get('/usuarios/me');
        if (res.data?.tipo !== 'super_admin') {
          navigate('/dashboard', { replace: true });
          return;
        }
        setAccessLoading(false);
      } catch (err) {
        navigate('/dashboard', { replace: true });
      }
    };

    validarAcesso();
  }, [navigate]);

  useEffect(() => {
    const carregarTipos = async () => {
      setLoadingTipos(true);
      try {
        const res = await api.get("/tipos-estabelecimento");
        const lista = Array.isArray(res.data) ? res.data : res.data?.resultados || [];
        setTipos(lista);
      } catch (err) {
        console.error("Erro ao carregar tipos:", err);
        toast.error("Erro ao carregar tipos de estabelecimento.");
      } finally {
        setLoadingTipos(false);
      }
    };

    carregarTipos();
  }, []);

  useEffect(() => {
    if (!adminUser.usuario) {
      setUsernameStatus("idle");
      return;
    }

    setUsernameStatus("loading");
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/super/usuarios/disponibilidade?usuario=${adminUser.usuario}`);
        setUsernameStatus(res.data?.usuario_disponivel ? "available" : "unavailable");
      } catch (err) {
        console.error("Erro ao verificar usuário:", err);
        setUsernameStatus("idle");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [adminUser.usuario]);

  useEffect(() => {
    const email = String(adminUser.email || "").trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email || !emailRegex.test(email)) {
      setEmailStatus("idle");
      return;
    }

    setEmailStatus("loading");
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/super/usuarios/disponibilidade?email=${encodeURIComponent(email)}`);
        setEmailStatus(res.data?.email_disponivel ? "available" : "unavailable");
      } catch (err) {
        console.error("Erro ao verificar e-mail:", err);
        setEmailStatus("idle");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [adminUser.email]);

  useEffect(() => {
    if (!estabelecimento.dominio) {
      setDominioStatus("idle");
      return;
    }

    setDominioStatus("loading");
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/super/estabelecimentos/disponibilidade?dominio=${estabelecimento.dominio}`);
        setDominioStatus(res.data?.dominio_disponivel ? "available" : "unavailable");
      } catch (err) {
        console.error("Erro ao verificar domínio:", err);
        setDominioStatus("error");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [estabelecimento.dominio]);

  useEffect(() => {
    if (!sugestoesDominio.length) {
      setSugestoesDominioStatus({});
      return;
    }

    const checar = async () => {
      const status = {};
      await Promise.all(
        sugestoesDominio.map(async (item) => {
          status[item] = "loading";
          try {
            const res = await api.get(`/super/estabelecimentos/disponibilidade?dominio=${item}`);
            status[item] = res.data?.dominio_disponivel ? "available" : "unavailable";
          } catch (err) {
            status[item] = "error";
          }
        })
      );
      setSugestoesDominioStatus(status);
    };

    checar();
  }, [sugestoesDominio]);

  const gerarEstabelecimentoDemo = async () => {
    const nomesEst = ["Barbearia", "Studio", "Espaço", "Salão", "Clínica", "Lounge"];
    const adj = ["Prime", "Urbano", "Central", "Elite", "Golden", "Nova Era"];
    const cidades = ["São Paulo", "Campinas", "Santos", "Rio de Janeiro", "Curitiba", "Belo Horizonte"];
    const estados = ["SP", "RJ", "PR", "MG"];
    const nomes = ["Leandro", "Mariana", "Bruno", "Carla", "Rafael", "Ana"];
    const sobrenomes = ["Silva", "Souza", "Oliveira", "Pereira", "Almeida", "Costa"];

    const nome = `${nomesEst[Math.floor(Math.random() * nomesEst.length)]} ${adj[Math.floor(Math.random() * adj.length)]}`;
    const cidade = cidades[Math.floor(Math.random() * cidades.length)];
    const estado = estados[Math.floor(Math.random() * estados.length)];
    const dominioBase = slugifyDominio(`${nome} ${cidade}`);
    const dominio = `${dominioBase}_${Math.floor(100 + Math.random() * 900)}`;

    const adminNome = `${nomes[Math.floor(Math.random() * nomes.length)]} ${sobrenomes[Math.floor(Math.random() * sobrenomes.length)]}`;
    const usuario = slugifyDominio(adminNome).slice(0, 20) || `admin_${Date.now()}`;

    const tipoId = tipos[0]?.id ? String(tipos[0].id) : "";

    setErros({});
    setEstabelecimento((prev) => ({
      ...prev,
      nome,
      dominio,
      tipo_id: tipoId,
      descricao: "Estabelecimento de teste gerado automaticamente.",
      endereco: "Rua das Flores, 123",
      cidade,
      estado,
      cep: maskCep("01001000"),
      telefone: maskTelefone("11999998888"),
      email: `contato+${Date.now()}@${dominio}.com`,
      documento_faturamento: maskCpfCnpj("24971563792"),
      instagram: `@${slugifyDominio(nome)}`,
      facebook: `${slugifyDominio(nome)}`,
      whatsapp: maskTelefone("11999997777"),
      site: `https://${dominio}.com`,
      hora_abertura: "09:00",
      hora_fechamento: "18:00",
    }));

    setAdminUser((prev) => ({
      ...prev,
      nome: adminNome,
      usuario,
      email: `admin+${Date.now()}@${dominio}.com`,
      telefone: maskTelefone("11988887777"),
    }));

    setProfissionalAdmin((prev) => ({
      ...prev,
      nome: adminNome,
      telefone: maskTelefone("11988887777"),
    }));

    toast.info("O administrador receberá um e-mail com link seguro para definir a senha.");

  };

  const solicitarSugestoes = async () => {
    const lista = gerarSugestoes(adminUser.nome);
    if (!lista.length) {
      toast.info("Informe o nome do administrador para gerar sugestões.");
      return;
    }

    setSugestoes(lista);
    setMostrarSugestoes(true);

    const status = {};
    await Promise.all(
      lista.map(async (item) => {
        status[item] = "loading";
        try {
          const res = await api.get(`/super/usuarios/disponibilidade?usuario=${item}`);
          status[item] = res.data?.usuario_disponivel ? "available" : "unavailable";
        } catch (err) {
          status[item] = "error";
        }
      })
    );
    setSugestoesStatus(status);
  };

  const validar = () => {
    const novo = {};

    const obrigatoriosEst = [
      "nome",
      "dominio",
      "tipo_id",
      "telefone",
      "documento_faturamento",
    ];
    obrigatoriosEst.forEach((campo) => {
      if (!estabelecimento[campo]) novo[campo] = "Campo obrigatório";
    });

    const docDigits = onlyDigits(estabelecimento.documento_faturamento);
    if (estabelecimento.documento_faturamento && docDigits.length !== 11 && docDigits.length !== 14) {
      novo.documento_faturamento = "CPF ou CNPJ inválido";
    }

    if (estabelecimento.dominio && !/^[a-z0-9_]{3,80}$/.test(estabelecimento.dominio)) {
      novo.dominio = "Domínio inválido. Use letras, números e _ (3-80 caracteres)";
    }

    if (dominioStatus === "unavailable") {
      novo.dominio = "Domínio já está em uso.";
    }

    const obrigatoriosAdmin = ["nome", "usuario", "email"];
    obrigatoriosAdmin.forEach((campo) => {
      if (!adminUser[campo]) novo[`admin_${campo}`] = "Campo obrigatório";
    });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (adminUser.email && !emailRegex.test(adminUser.email.trim())) {
      novo.admin_email = "E-mail inválido";
    }

    if (emailStatus === "unavailable") {
      novo.admin_email = "Este e-mail já está em uso.";
    }

    if (!criarProfissionalAdmin) {
      if (!profissionalAdmin.nome) novo.prof_nome = "Campo obrigatório";
    }

    setErros(novo);
    return Object.keys(novo).length === 0;
  };

  const podeSalvar = useMemo(() => {
    return (
      estabelecimento.nome &&
      estabelecimento.dominio &&
      estabelecimento.tipo_id &&
      estabelecimento.telefone &&
      estabelecimento.documento_faturamento &&
      adminUser.nome &&
      adminUser.usuario &&
      adminUser.email &&
      (criarProfissionalAdmin || profissionalAdmin.nome) &&
      usernameStatus !== "unavailable" &&
      emailStatus !== "unavailable" &&
      dominioStatus !== "unavailable"
    );
  }, [estabelecimento, adminUser, criarProfissionalAdmin, profissionalAdmin, usernameStatus, emailStatus, dominioStatus]);

  const handleSubmit = async () => {
    if (!validar()) {
      toast.error("Revise os campos obrigatórios.");
      return;
    }

    setLoadingSave(true);
    try {
      const { imagem_logo, ...estabelecimentoSemImagem } = estabelecimento;
      const payload = {
        estabelecimento: {
          ...estabelecimentoSemImagem,
          telefone: onlyDigits(estabelecimento.telefone),
          documento_faturamento: onlyDigits(estabelecimento.documento_faturamento),
          cep: onlyDigits(estabelecimento.cep),
          whatsapp: onlyDigits(estabelecimento.whatsapp),
        },
        adminUser: {
          ...adminUser,
          email: String(adminUser.email || "").trim().toLowerCase(),
          telefone: onlyDigits(adminUser.telefone),
        },
        criarProfissionalAdmin,
        profissionalAdmin: criarProfissionalAdmin ? null : {
          nome: profissionalAdmin.nome,
          telefone: onlyDigits(profissionalAdmin.telefone),
        },
      };

      await api.post("/super/estabelecimentos", payload);
      toast.success("Estabelecimento criado com sucesso!");
      setTimeout(() => navigate("/estabelecimentos"), 1200);
    } catch (err) {
      console.error("Erro ao criar estabelecimento:", err);
      const status = err.response?.status;
      if (status === 409) {
        toast.error(err.response?.data?.mensagem || "Este e-mail ou usuário já está em uso.");
      } else if (status === 403) {
        toast.error("Acesso restrito ao super admin.");
      } else {
        toast.error("Erro ao criar estabelecimento.");
      }
    } finally {
      setLoadingSave(false);
    }
  };

  if (accessLoading) {
    return (
      <div className="novo-estabelecimento-page">
        <div className="container">
          <div className="card-block" style={{ padding: 20 }}>
            Carregando...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="novo-estabelecimento-page">
      <ToastContainer />
      {loadingSave && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, color: "#fff" }}>
            <Spinner size={48} />
            <span>Salvando...</span>
          </div>
        </div>
      )}
      <div className="container">
        <div className="page-header">
          <div>
            <div className="page-title">
              <Store size={22} />
              <h1>Novo Estabelecimento</h1>
            </div>
            <p className="muted">Preencha os campos abaixo para criar um novo estabelecimento.</p>
          </div>
          <div className="page-header-actions">
            <button
              type="button"
              className="btn-generate"
              onClick={gerarEstabelecimentoDemo}
              disabled={loadingSave || loadingTipos}
              title="Gerar dados de teste"
            >
              <RefreshCw size={16} />
              Gerar estabelecimento
            </button>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <Store size={18} />
            <h2>Dados do Estabelecimento</h2>
          </div>

          <div className="form-grid two-cols">
            <div>
              <label className="field-label">Nome Fantasia *</label>
              <input
                className={`field ${erros.nome ? "input-error" : ""}`}
                value={estabelecimento.nome}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, nome: e.target.value }))}
                placeholder="Ex.: Barbearia Central"
              />
              {erros.nome && <div className="error-text">{erros.nome}</div>}

              <label className="field-label" style={{ marginTop: 12 }}>Domínio *</label>
              <input
                className={`field ${erros.dominio ? "input-error" : ""}`}
                value={estabelecimento.dominio}
                readOnly
                placeholder="Selecione uma sugestão"
              />
              {dominioStatus === "available" && (
                <div className="inline-status success">✅ Disponível</div>
              )}
              {dominioStatus === "unavailable" && (
                <div className="inline-status error">❌ Indisponível</div>
              )}
              {dominioStatus === "loading" && (
                <div className="inline-status muted">Verificando...</div>
              )}
              {erros.dominio && <div className="error-text">{erros.dominio}</div>}

              <div className="username-hint">
                Escolha o domínio do estabelecimento
                <button type="button" className="link-button" onClick={abrirSugestoesDominio}>
                  <RefreshCw size={14} /> Gerar opções
                </button>
              </div>

              {mostrarSugestoesDominio && (
                <div
                  className="modal-overlay"
                  onClick={() => setMostrarSugestoesDominio(false)}
                >
                  <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                    <div className="modal-header">
                      <h3>Escolha um domínio</h3>
                      <button className="icon-button" onClick={() => setMostrarSugestoesDominio(false)}>✖</button>
                    </div>
                    <div className="modal-body">
                      {sugestoesDominio.length === 0 && <div className="muted">Nenhuma sugestão gerada.</div>}
                      <div className="suggestions modal-suggestions">
                        {sugestoesDominio.map((s) => (
                          <button
                            key={s}
                            type="button"
                            className={`suggestion ${sugestoesDominioStatus[s]}`}
                            onClick={() => {
                              if (sugestoesDominioStatus[s] === "available") {
                                setEstabelecimento((prev) => ({ ...prev, dominio: s }));
                                setMostrarSugestoesDominio(false);
                              }
                            }}
                          >
                            <span>{s}</span>
                            <span>
                              {sugestoesDominioStatus[s] === "loading" && "..."}
                              {sugestoesDominioStatus[s] === "available" && "✅"}
                              {sugestoesDominioStatus[s] === "unavailable" && "❌"}
                              {sugestoesDominioStatus[s] === "error" && "⚠️"}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="field-label">Tipo do estabelecimento *</label>
              <select
                className={`field ${erros.tipo_id ? "input-error" : ""}`}
                value={estabelecimento.tipo_id}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, tipo_id: e.target.value }))}
                disabled={loadingTipos}
              >
                <option value="">Selecione...</option>
                {tipos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nome || t.descricao || `Tipo #${t.id}`}
                  </option>
                ))}
              </select>
              {erros.tipo_id && <div className="error-text">{erros.tipo_id}</div>}
            </div>

            <div>
              <label className="field-label">Telefone *</label>
              <input
                className={`field ${erros.telefone ? "input-error" : ""}`}
                value={estabelecimento.telefone}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, telefone: maskTelefone(e.target.value) }))}
                placeholder="(11) 99999-9999"
              />
              {erros.telefone && <div className="error-text">{erros.telefone}</div>}
            </div>

            <div>
              <label className="field-label">CPF/CNPJ de cobrança *</label>
              <input
                className={`field ${erros.documento_faturamento ? "input-error" : ""}`}
                value={estabelecimento.documento_faturamento}
                onChange={(e) =>
                  setEstabelecimento((s) => ({
                    ...s,
                    documento_faturamento: maskCpfCnpj(e.target.value),
                  }))
                }
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                inputMode="numeric"
              />
              {erros.documento_faturamento && (
                <div className="error-text">{erros.documento_faturamento}</div>
              )}
            </div>

            <div>
              <label className="field-label">Email</label>
              <input
                className="field"
                type="email"
                value={estabelecimento.email}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, email: e.target.value }))}
                placeholder="contato@empresa.com"
              />
            </div>

            <div>
              <label className="field-label">CEP</label>
              <input
                className={`field ${erros.cep ? "input-error" : ""}`}
                value={estabelecimento.cep}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, cep: maskCep(e.target.value) }))}
                onBlur={(e) => handleCepBlur(e, setEstabelecimento)}
                placeholder="00000-000"
              />
              {erros.cep && <div className="error-text">{erros.cep}</div>}
            </div>

            <div>
              <label className="field-label">Rua / Endereço</label>
              <input
                className={`field ${erros.endereco ? "input-error" : ""}`}
                value={estabelecimento.endereco}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, endereco: e.target.value }))}
                placeholder="Rua / avenida"
              />
              {erros.endereco && <div className="error-text">{erros.endereco}</div>}
            </div>

            <div>
              <label className="field-label">Número</label>
              <input
                className={`field ${erros.numero ? "input-error" : ""}`}
                value={estabelecimento.numero}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, numero: e.target.value }))}
                placeholder="Número"
                maxLength={20}
              />
              {erros.numero && <div className="error-text">{erros.numero}</div>}
            </div>

            <div>
              <label className="field-label">Bairro</label>
              <input
                className="field"
                value={estabelecimento.bairro || ""}
                readOnly
                placeholder="Preenchido pelo CEP"
                title="Obtido automaticamente pelo CEP (ViaCEP)"
              />
            </div>

            <div>
              <label className="field-label">Cidade</label>
              <input
                className={`field ${erros.cidade ? "input-error" : ""}`}
                value={estabelecimento.cidade}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, cidade: e.target.value }))}
                placeholder="Cidade"
              />
              {erros.cidade && <div className="error-text">{erros.cidade}</div>}
            </div>

            <div>
              <label className="field-label">Estado (UF)</label>
              <select
                className={`field ${erros.estado ? "input-error" : ""}`}
                value={estabelecimento.estado}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, estado: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {estadosUF.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
              {erros.estado && <div className="error-text">{erros.estado}</div>}
            </div>

            <div>
              <label className="field-label">Instagram</label>
              <input
                className="field"
                value={estabelecimento.instagram}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, instagram: e.target.value }))}
              />
            </div>

            <div>
              <label className="field-label">Facebook</label>
              <input
                className="field"
                value={estabelecimento.facebook}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, facebook: e.target.value }))}
              />
            </div>

            <div>
              <label className="field-label">WhatsApp</label>
              <input
                className="field"
                value={estabelecimento.whatsapp}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, whatsapp: maskTelefone(e.target.value) }))}
              />
            </div>

            <div>
              <label className="field-label">Site</label>
              <input
                className="field"
                value={estabelecimento.site}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, site: e.target.value }))}
              />
            </div>

            <div>
              <label className="field-label">Hora abertura</label>
              <FilaZeroTimeSelect
                value={estabelecimento.hora_abertura}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, hora_abertura: e.target.value }))}
              />
            </div>

            <div>
              <label className="field-label">Hora fechamento</label>
              <FilaZeroTimeSelect
                value={estabelecimento.hora_fechamento}
                onChange={(e) => setEstabelecimento((s) => ({ ...s, hora_fechamento: e.target.value }))}
              />
            </div>
          </div>

        </div>

        <div className="form-section">
          <div className="section-header">
            <User size={18} />
            <h2>Dados do Administrador</h2>
          </div>
          <p className="muted" style={{ marginBottom: 14 }}>
            O administrador terá acesso ao painel para gerenciar o estabelecimento.
          </p>

          <div className="form-grid two-cols">
            <div>
              <label className="field-label">Nome do Administrador *</label>
              <input
                className={`field ${erros.admin_nome ? "input-error" : ""}`}
                value={adminUser.nome}
                onChange={(e) => setAdminUser((s) => ({ ...s, nome: e.target.value }))}
                placeholder="Nome completo"
              />
              {erros.admin_nome && <div className="error-text">{erros.admin_nome}</div>}
            </div>

            <div>
              <label className="field-label">Usuário de acesso *</label>
              <input
                className={`field ${erros.admin_usuario ? "input-error" : ""}`}
                value={adminUser.usuario}
                disabled
                placeholder="Bloqueado — selecione uma sugestão"
              />
              {usernameStatus === "available" && (
                <div className="inline-status success">✅ Disponível</div>
              )}
              {usernameStatus === "unavailable" && (
                <div className="inline-status error">❌ Indisponível</div>
              )}
              {usernameStatus === "loading" && (
                <div className="inline-status muted">Verificando...</div>
              )}
              <div className="username-hint">
                Escolha seu usuário de acesso
                <button type="button" className="link-button" onClick={solicitarSugestoes}>
                  <RefreshCw size={14} /> Gerar opções
                </button>
              </div>
              {mostrarSugestoes && (
                <div
                  className="modal-overlay"
                  onClick={() => setMostrarSugestoes(false)}
                >
                  <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                    <div className="modal-header">
                      <h3>Escolha um usuário</h3>
                      <button className="icon-button" onClick={() => setMostrarSugestoes(false)}>✖</button>
                    </div>
                    <div className="modal-body">
                      {sugestoes.length === 0 && <div className="muted">Nenhuma sugestão gerada.</div>}
                      <div className="suggestions modal-suggestions">
                        {sugestoes.map((s) => (
                          <button
                            key={s}
                            type="button"
                            className={`suggestion ${sugestoesStatus[s]}`}
                            onClick={() => {
                              if (sugestoesStatus[s] === "available") {
                                setAdminUser((prev) => ({ ...prev, usuario: s }));
                                setMostrarSugestoes(false);
                              }
                            }}
                          >
                            <span>{s}</span>
                            <span>
                              {sugestoesStatus[s] === "loading" && "..."}
                              {sugestoesStatus[s] === "available" && "✅"}
                              {sugestoesStatus[s] === "unavailable" && "❌"}
                              {sugestoesStatus[s] === "error" && "⚠️"}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {erros.admin_usuario && <div className="error-text">{erros.admin_usuario}</div>}
            </div>

            <div>
              <label className="field-label">Email do Administrador *</label>
              <input
                className={`field ${erros.admin_email ? "input-error" : ""}`}
                value={adminUser.email}
                onChange={(e) => setAdminUser((s) => ({ ...s, email: e.target.value }))}
                type="email"
              />
              {emailStatus === "available" && (
                <div className="inline-status success">✅ E-mail disponível</div>
              )}
              {emailStatus === "unavailable" && (
                <div className="inline-status error">❌ E-mail já cadastrado</div>
              )}
              {emailStatus === "loading" && (
                <div className="inline-status muted">Verificando e-mail...</div>
              )}
              {erros.admin_email && <div className="error-text">{erros.admin_email}</div>}
            </div>

            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "12px 14px", marginBottom: 12, color: "#1e3a8a", fontSize: 14 }}>
              A senha inicial não é definida aqui. O administrador receberá um e-mail com link seguro para criar a própria senha.
            </div>

            <div>
              <label className="field-label">Telefone do Administrador</label>
              <input
                className="field"
                value={adminUser.telefone}
                onChange={(e) => setAdminUser((s) => ({ ...s, telefone: maskTelefone(e.target.value) }))}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <ShieldCheck size={18} />
            <h2>Profissional Inicial</h2>
          </div>

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={criarProfissionalAdmin}
              onChange={(e) => setCriarProfissionalAdmin(e.target.checked)}
            />
            Criar profissional administrador inicial com os mesmos dados do administrador
          </label>

          {!criarProfissionalAdmin && (
            <div className="form-grid two-cols" style={{ marginTop: 12 }}>
              <div>
                <label className="field-label">Nome do profissional *</label>
                <input
                  className={`field ${erros.prof_nome ? "input-error" : ""}`}
                  value={profissionalAdmin.nome}
                  onChange={(e) => setProfissionalAdmin((s) => ({ ...s, nome: e.target.value }))}
                />
                {erros.prof_nome && <div className="error-text">{erros.prof_nome}</div>}
              </div>
              <div>
                <label className="field-label">Telefone</label>
                <input
                  className="field"
                  value={profissionalAdmin.telefone}
                  onChange={(e) => setProfissionalAdmin((s) => ({ ...s, telefone: maskTelefone(e.target.value) }))}
                />
              </div>
            </div>
          )}
        </div>

        

        <div className="form-actions">
          <button className="btn btn-outline" type="button" onClick={() => navigate(-1)}>
            Cancelar
          </button>
          <button className="btn" type="button" onClick={handleSubmit} disabled={!podeSalvar || loadingSave}>
            {loadingSave ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Spinner size={16} />
                Salvando...
              </span>
            ) : (
              "+ Criar Estabelecimento"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NovoEstabelecimento;
